import { useQuery } from '@tanstack/vue-query'

import { Ref, computed } from 'vue'
import { RouteLocationRaw } from 'vue-router'

import { useUrl } from '@/composables/url-helpers'
import { DEFAULT_MAIN_PAGE_SIZE } from '@/constants/general'
import { useI18nAddon } from '@/i18n/i18n'
import { useAppStore } from '@/store/app-store'
import { CategoryDataType, MainCategoriesType } from '@/types'
import { shuffle, unzipShortGame, zipAPIGame } from '@/utils/helpers'
import { getGamesByCategories } from '@/utils/requests/games'

const PLACEHOLDER_DATA = {
    id: '',
    title: '',
    location: '',
    games: [],
}

export type ShortCategory = {
    id: MainCategoriesType
    title: string
    location: RouteLocationRaw
}

export const useTrendingData = (options: { enabled?: Ref<boolean> }) => {
    const { currentLocale } = useI18nAddon()
    const { getCategoryRoute } = useUrl()
    const appStore = useAppStore()
    const queryKey = computed(() => ['trending_now', DEFAULT_MAIN_PAGE_SIZE, currentLocale.value])
    const currentTrending = computed(() => data.value || PLACEHOLDER_DATA)
    const unzippedTrendingWithGames = computed(() => currentTrending.value.games.map(unzipShortGame))

    function mapCategory(data: CategoryDataType) {
        const [trending] = data.categories.map(({ id, hru, title, games }) => ({
            id: id as MainCategoriesType,
            title,
            location: getCategoryRoute({ hru, type: 'category' }),
            games: shuffle(games.map(zipAPIGame)),
        }))
        return trending
    }
    const { data } = useQuery({
        queryKey,
        queryFn: () =>
            getGamesByCategories({
                categoriesIds: ['trending_now'],
                pagination: { page: 1, pageSize: DEFAULT_MAIN_PAGE_SIZE },
                platform: appStore.platform,
                withDebugInfo: appStore.isDebugMode,
                locale: currentLocale.value,
                ...options,
            }).then(mapCategory),
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
    })

    return {
        unzippedTrendingWithGames,
        currentTrending,
    }
}
